import {PixelPlan} from './PixelPlan';
import React, {useEffect, useRef, useState} from 'react';
import {BillingCycle, TierOrPlan} from '../../../utils/tier';
import {apiPost} from '../../../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../../../constants';
import {toast, ToastContainer} from 'react-toastify';
import {Dialog, DialogContent} from '../../../ui-components/Modal';
import {OrganizationTier, UserProfileInterface} from '../../../reducers/users';
import {BaseScreen} from '../Screen';
import {ErrorTags, EventNames, ScreenNames} from '../../../lib/analytics/events';
import {UserLoader} from '../../../components/UserLoader';
import {loadScript} from '../../../lib/load-script';
import {Invoice} from '../../../api/types/billing';
import {nFetch} from '../../../api/nFetch';
import Tracker from '../../../lib/analytics/tracker';
import {InvoicePaymentHandler} from '../billing/InvoicePaymentHandler';
import {useAppDispatch} from '../../../store';
import {refreshUserProfile} from '../../../actions/userProfile';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {RotatedArrowIcon, WalletIcon} from '../../../ui-components/Iconpack';
import {TrackGoogleEvent} from '../../../lib/analytics/gtag';
import {Button} from '../../../ui-components/Button/button';
import PixelLogoHeader from '../../../assets/icons/pixel-logo-dialog.svg';

export interface CallbackRequest {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    message: string;
    source: string;
}

function getPriceStringForPlan(plan: TierOrPlan, cycle: BillingCycle): string {
    const prices: Record<TierOrPlan, Record<BillingCycle, string>> = {
        [TierOrPlan.Free]: {[BillingCycle.Monthly]: '$0', [BillingCycle.Yearly]: '$0', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Referral]: {[BillingCycle.Monthly]: '$0', [BillingCycle.Yearly]: '$0', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Pro]: {[BillingCycle.Monthly]: '$39', [BillingCycle.Yearly]: '$374', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.PixelClassic]: {
            [BillingCycle.Monthly]: '$5',
            [BillingCycle.Yearly]: '',
            [BillingCycle.Lifetime]: '',
        },
        [TierOrPlan.PixelStudio]: {
            [BillingCycle.Monthly]: '$10',
            [BillingCycle.Yearly]: '',
            [BillingCycle.Lifetime]: '',
        },
        [TierOrPlan.Hobbyist]: {
            [BillingCycle.Monthly]: '$10',
            [BillingCycle.Yearly]: '$96',
            [BillingCycle.Lifetime]: '',
        },
        [TierOrPlan.Custom]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Signature]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Elite]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Explorer]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Ignite]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
        [TierOrPlan.Students]: {[BillingCycle.Monthly]: '', [BillingCycle.Yearly]: '', [BillingCycle.Lifetime]: ''},
    };

    return prices[plan]?.[cycle] || '';
}

function isPaidTier(tier: TierOrPlan) {
    return (
        tier === TierOrPlan.Hobbyist ||
        tier === TierOrPlan.Pro ||
        tier === TierOrPlan.PixelClassic ||
        tier === TierOrPlan.PixelStudio ||
        tier === TierOrPlan.Custom
    );
}

function PlanInvoiceHandler(props: {
    invoice: Invoice;
    setLoading: (loading: boolean) => void;
    onSuccess: (success: boolean) => void;
}) {
    return new InvoicePaymentHandler({
        invoice: props.invoice,
        onNext: (invoice) => {
            if (invoice.status === 'PAID') {
                toast.success('Payment successful!');
                props.onSuccess(true);
            } else if (invoice.status === 'CANCELLED') {
                toast.error(`Payment cancelled. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onSuccess(false);
            } else {
                toast.error(`Payment failed. ${InvoicePaymentHandler.REFUND_MSG}`);
                props.onSuccess(false);
            }
        },
        onError: (error) => {
            toast.error(error.message);
            props.onSuccess(false);
        },
        onLoading: (loading) => {
            props.setLoading(loading);
        },
        onProcessing: (processing) => {
            if (processing) {
                toast.info('Processing payment...');
            }
        },
        cancelInvoice: true,
    });
}

function HobbyistPlan(props: {
    tier: TierOrPlan;
    onSelect: (tierOrgPlan: TierOrPlan) => void;
    onSwitch: (success: boolean) => void;
    loading: boolean;
    invoice?: Invoice;
    expiry?: number;
    billingCycle: BillingCycle;
}) {
    const currentTier = props.tier === TierOrPlan.Hobbyist;
    const mut = useRef<InvoicePaymentHandler | undefined>(undefined);
    const [localLoading, setLocalLoading] = useState<boolean>(false);

    React.useEffect(() => {
        if (props.invoice && !mut.current) {
            mut.current = PlanInvoiceHandler({
                invoice: props.invoice,
                setLoading: setLocalLoading,
                onSuccess: props.onSwitch,
            });
            mut.current?.next();
        }
    });
    return (
        <PixelPlan
            title={'Hobbyist'}
            subtitle={'Best for individuals'}
            price={getPriceStringForPlan(TierOrPlan.Hobbyist, props.billingCycle)}
            titleGradient={'from-[#B01EFF] to-[#E1467C]'}
            benefits={[
                {text: 'Exclusive access to PixelStudio'},
                {text: 'Upto 50 GB bandwidth per month, thereafter $0.19/GB'},
                {text: 'Upto 10 GB storage included, thereafter $0.10/GB'},
                {text: 'Unlimited background removal transformation'},
                {text: 'AI-based annotation generation'},
                {
                    text: 'Digital Asset Management (DAM)',
                    subBenefits: [
                        {text: 'Advanced features for organizing, storing, and retrieving your media assets included'},
                        {text: 'Folder permission capabilities'},
                        {text: 'Smart search functionality'},
                    ],
                },
                {text: 'Plugins: Chrome and WordPress'},
            ]}
            ctaText={currentTier ? 'Current Plan' : (isPaidTier(props.tier) ? 'Switch to ' : 'Select ') + 'Hobbyist'}
            expiry={props.expiry}
            currentPlan={currentTier}
            onPlanSelect={currentTier ? () => {} : () => props.onSelect(TierOrPlan.Hobbyist)}
            loading={props.loading || localLoading}
            horizontalCard={false}
            billingCycle={props.billingCycle}
        />
    );
}

function ClassicPlan(props: {
    tier: TierOrPlan;
    onSelect: (tierOrgPlan: TierOrPlan) => void;
    onSwitch: (success: boolean) => void;
    loading: boolean;
    invoice?: Invoice;
    expiry?: number;
    billingCycle: BillingCycle;
}) {
    const currentTier = props.tier === TierOrPlan.PixelClassic;
    const mut = useRef<InvoicePaymentHandler | undefined>(undefined);
    const [localLoading, setLocalLoading] = useState<boolean>(false);

    React.useEffect(() => {
        if (props.invoice && !mut.current) {
            mut.current = PlanInvoiceHandler({
                invoice: props.invoice,
                setLoading: setLocalLoading,
                onSuccess: props.onSwitch,
            });
            mut.current?.next();
        }
    });
    return (
        <PixelPlan
            title={'Pixel Classic'}
            subtitle={'Simple editor to do you edits'}
            price={getPriceStringForPlan(TierOrPlan.PixelClassic, props.billingCycle)}
            titleGradient={'from-[#B01EFF] to-[#E1467C]'}
            benefits={[
                {text: 'Upto 50 GB bandwidth per month, thereafter $0.19/GB'},
                {text: 'Upto 10 GB storage included, thereafter $0.10/GB'},
                {text: 'Unlimited background removal transformation'},
                {text: 'AI-based annotation generation'},
                {text: 'Transformations of to Webp, Jpeg and Png formats'},
                {text: 'API support'},
                {text: 'Security features like referer-based Hotlinking Protection'},
                {text: '5 custom domains like img.yourdomain.com'},
                {text: 'Up to 10 users; Role-based multi-user administration'},
                {text: 'Remote storage (BYOS): Store your images elsewhere and use our CDN and transformations'},
                {
                    text: 'Digital Asset Management (DAM)',
                    subBenefits: [
                        {text: 'Advanced features for organizing, storing, and retrieving your media assets included'},
                        {text: 'Folder permission capabilities'},
                        {text: 'Smart search functionality'},
                    ],
                },
                {text: 'Plugins: Chrome and WordPress'},
            ]}
            ctaText={
                currentTier ? 'Current Plan' : (isPaidTier(props.tier) ? 'Switch to ' : 'Select ') + 'PixelClassic'
            }
            expiry={props.expiry}
            currentPlan={currentTier}
            onPlanSelect={currentTier ? () => {} : () => props.onSelect(TierOrPlan.PixelClassic)}
            loading={props.loading || localLoading}
            horizontalCard={false}
            billingCycle={props.billingCycle}
        />
    );
}

function StudioPlan(props: {
    tier: TierOrPlan;
    onSelect: (tierOrgPlan: TierOrPlan) => void;
    onSwitch: (success: boolean) => void;
    loading: boolean;
    invoice?: Invoice;
    expiry?: number;
    billingCycle: BillingCycle;
}) {
    const currentTier = props.tier === TierOrPlan.PixelStudio;
    const mut = useRef<InvoicePaymentHandler | undefined>(undefined);
    const [localLoading, setLocalLoading] = useState<boolean>(false);

    React.useEffect(() => {
        if (props.invoice && !mut.current) {
            mut.current = PlanInvoiceHandler({
                invoice: props.invoice,
                setLoading: setLocalLoading,
                onSuccess: props.onSwitch,
            });
            mut.current?.next();
        }
    });
    return (
        <PixelPlan
            title={'Pixel Studio'}
            subtitle={'Unleash your creativity'}
            price={getPriceStringForPlan(TierOrPlan.PixelStudio, props.billingCycle)}
            titleGradient={'from-[#B01EFF] to-[#E1467C]'}
            benefits={[
                {text: 'Exclusive access to PixelStudio'},
                {text: 'Upto 50 GB bandwidth per month, thereafter $0.19/GB'},
                {text: 'Upto 10 GB storage included, thereafter $0.10/GB'},
                {text: 'Unlimited background removal transformation'},
                {text: 'AI-based annotation generation'},
                {text: 'API support'},
                {text: 'Security features like referer-based Hotlinking Protection'},
                {text: '5 custom domains like img.yourdomain.com'},
                {text: 'Up to 10 users; Role-based multi-user administration'},
                {text: 'Remote storage (BYOS): Store your images elsewhere and use our CDN and transformations'},
                {
                    text: 'Digital Asset Management (DAM)',
                    subBenefits: [
                        {text: 'Advanced features for organizing, storing, and retrieving your media assets included'},
                        {text: 'Folder permission capabilities'},
                        {text: 'Smart search functionality'},
                    ],
                },
                {text: 'Plugins: Chrome and WordPress'},
            ]}
            ctaText={currentTier ? 'Current Plan' : (isPaidTier(props.tier) ? 'Switch to ' : 'Select ') + 'PixelStudio'}
            expiry={props.expiry}
            currentPlan={currentTier}
            onPlanSelect={currentTier ? () => {} : () => props.onSelect(TierOrPlan.PixelStudio)}
            loading={props.loading || localLoading}
            horizontalCard={false}
            billingCycle={props.billingCycle}
        />
    );
}

function ProPlan(props: {
    tier: TierOrPlan;
    onSelect: (tierOrgPlan: TierOrPlan) => void;
    onSwitch: (success: boolean) => void;
    loading: boolean;
    invoice?: Invoice;
    expiry?: number;
    billingCycle: BillingCycle;
}) {
    const currentTier = props.tier === TierOrPlan.Pro;
    const mut = useRef<InvoicePaymentHandler | undefined>(undefined);
    const [localLoading, setLocalLoading] = useState<boolean>(false);

    React.useEffect(() => {
        if (props.invoice && !mut.current) {
            mut.current = PlanInvoiceHandler({
                invoice: props.invoice,
                setLoading: setLocalLoading,
                onSuccess: props.onSwitch,
            });
            mut.current?.next();
        }
    });
    return (
        <PixelPlan
            title={'Professional'}
            subtitle={'Best for small businesses'}
            price={getPriceStringForPlan(TierOrPlan.Pro, props.billingCycle)}
            titleGradient={'from-[#B39FFF] to-[#6A1ED2]'}
            infoText={'Everything in Hobbyist plan, plus:'}
            benefits={[
                {text: 'Upto 50 GB bandwidth per month, thereafter $10/20 GB'},
                {text: 'Upto 10 GB storage included, thereafter $1/GB storage'},
                {text: '1 custom domain like img.yourdomain.com'},
                {text: 'Automated image analysis reports'},
                {text: 'One-time 30 minute consultation with an image optimization expert'},
                {text: 'Live chat & 12-hr SLA support tickets'},
                {text: 'Up to 5 users; Role-based multi-user administration'},
                {text: 'Security features like referer-based Hotlinking Protection'},
                {text: 'Remote storage (BYOS): Store your images elsewhere and use our CDN and transformations'},
            ]}
            ctaText={currentTier ? 'Current Plan' : (isPaidTier(props.tier) ? 'Switch to ' : 'Select ') + 'Pro'}
            expiry={props.expiry}
            currentPlan={currentTier}
            onPlanSelect={currentTier ? () => {} : () => props.onSelect(TierOrPlan.Pro)}
            loading={props.loading || localLoading}
            horizontalCard={false}
            billingCycle={props.billingCycle}
        />
    );
}

function CustomPlan(props: {
    tier: TierOrPlan;
    onSelect: (tierOrgPlan: TierOrPlan) => void;
    loading: boolean;
    orgTier?: OrganizationTier;
    billingCycle: BillingCycle;
}) {
    const currentTier = props.tier === TierOrPlan.Custom;
    let info = '';
    if (!props.orgTier?.name) {
        info = 'Everything in Professional plan, plus:';
    }
    return (
        <PixelPlan
            title={props.orgTier?.name ?? 'Custom'}
            subtitle={props.orgTier?.description ?? 'Bandwidth consumption over 1TB / month ?'}
            titleGradient={'from-[#00C6FB] to-[#005BEA]'}
            infoText={info}
            benefits={
                props.orgTier?.perks?.map((x) => ({text: x.description})) ?? [
                    {text: 'Dedicated integration & support engineer'},
                    {text: 'Custom CDN integration with PerceptPixel'},
                    {text: 'Origin Shield for stability & performance'},
                    {text: 'Custom cache-control policy & redirects'},
                    {
                        text: 'Digital Asset Management (DAM): Advanced features for organizing, storing, and retrieving your media assets included',
                    },
                ]
            }
            ctaText={props.orgTier?.tier_mgmt?.text ?? (currentTier ? 'Current Plan' : 'Contact Us')}
            currentPlan={currentTier}
            onPlanSelect={
                props.orgTier?.tier_mgmt?.url
                    ? () => {
                          window.open(props.orgTier?.tier_mgmt?.url, '_blank');
                      }
                    : currentTier
                      ? () => {}
                      : () => props.onSelect(TierOrPlan.Custom)
            }
            loading={props.loading}
            horizontalCard={false}
            billingCycle={props.billingCycle}
        />
    );
}

function PixelPlansScreenWithUser(props: {user: UserProfileInterface; rzpScriptReady: boolean}) {
    const {user} = props;
    const org = user.currentOrganization;
    const mut = useRef(false);
    const pmntMut = useRef<InvoicePaymentHandler | undefined>(undefined);
    const dispatch = useAppDispatch();

    const location = useLocation();
    const qp = new URLSearchParams(location.search);
    const cycle = qp.get('billingCycle');

    const [currentTier, setCurrentTier] = useState<TierOrPlan>(org.tier?.type || TierOrPlan.Free);

    const [selectedTier, setSelectedTier] = useState<TierOrPlan | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [showWallet, setShowWallet] = useState(false);
    const [payByWallet, setPayByWallet] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);

    const [invoice, setInvoice] = useState<Invoice | undefined>(undefined);

    const [contactUsPopup, setContactUsPopup] = useState<boolean>(false);
    const [contactUsRequest, setContactUsRequest] = useState<boolean>(false);
    const [billingCycle, setBillingCycle] = useState<BillingCycle>(
        cycle ? (cycle as BillingCycle) : BillingCycle.Monthly,
    );

    const {planId} = useParams();
    const navigate = useNavigate();

    async function onCloseContactUs() {
        setContactUsPopup(false);

        const callbackReq: CallbackRequest = {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone_number: '',
            message: 'Custom Plans Query',
            source: 'DASHBOARD',
        };
        if (!contactUsRequest) {
            await apiPost<any>(SWFT_DIGITAL_API_HOST + '/callback/request', callbackReq);
            setContactUsRequest(true);
        }
        toast.success('Our team will reach out to you shortly!');
    }

    const proceedToPayment = async () => {
        setShowWallet(false);
        const result = await nFetch<Invoice>(
            `/billing/start_for_org/${org.uid}`,
            {
                method: 'POST',
                body: JSON.stringify({
                    tier: selectedTier,
                    billing_cycle: billingCycle,
                    useCoins: payByWallet,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            undefined,
            false,
        );
        if (!!result.error) {
            Tracker.trackError(result.error, ErrorTags.INVOICE_START_ERROR);
            toast.error(result.error.message);
            setLoading(false);
            return;
        }
        setPayByWallet(false);
        setInvoice(result.data);
    };

    const onPlanSelect = async (choice: TierOrPlan) => {
        Tracker.trackEvent(EventNames.SELECT_PLAN_CLICKED, {}, {plan: choice});
        TrackGoogleEvent({action: `${choice} CLICKED`, label: 'Dashboard'});

        if (loading) {
            console.error('possible double click, ignoring');
            return;
        }

        setSelectedTier(choice);
        setLoading(true);

        if (choice === 'CUSTOM') {
            setContactUsPopup(true);
            setLoading(false);
            return;
        }

        Tracker.trackEvent(EventNames.INVOICE_START);
        const walletBalance = await nFetch<any>(`/reward/wallet?org_uid=${org.uid}`).then((res) => {
            if (!!res.error) throw res.error;
            setWalletBalance(res.data.balance);
            return res.data.balance;
        });
        if (walletBalance === 0) {
            const result = await nFetch<Invoice>(
                `/billing/start_for_org/${org.uid}`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        tier: choice,
                        billing_cycle: billingCycle,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
                undefined,
                false,
            );
            if (!!result.error) {
                Tracker.trackError(result.error, ErrorTags.INVOICE_START_ERROR);
                toast.error(result.error.message);
                setLoading(false);
                return;
            }
            setInvoice(result.data);
        } else {
            setShowWallet(true);
        }
    };

    useEffect(() => {
        if (mut.current) {
            return;
        }
        mut.current = true;
        if (!!planId) {
            const plan = planId.toUpperCase();
            if (Object.values(TierOrPlan).some((t) => t === plan)) {
                onPlanSelect(plan as TierOrPlan).then(() => {
                    navigate('/settings/plans', {replace: true});
                });
            }
        }
    });

    useEffect(() => {
        if (
            invoice &&
            selectedTier &&
            ['IGNITE', 'EXPLORER', 'ELITE', 'SIGNATURE', 'STUDENTS'].includes(selectedTier)
        ) {
            pmntMut.current = PlanInvoiceHandler({
                invoice: invoice,
                setLoading: setLoading,
                onSuccess: (success) => {
                    setLoading(false);
                    setSelectedTier(undefined);
                    if (success) {
                        setCurrentTier(selectedTier);
                        dispatch(refreshUserProfile());
                    } else {
                        setInvoice(undefined);
                    }
                },
            });
            pmntMut.current?.next();
        }
    }, [invoice]);

    if (
        org.tier?.type === TierOrPlan.Custom ||
        org.tier?.type === TierOrPlan.Signature ||
        org.tier?.type === TierOrPlan.Elite ||
        org.tier?.type === TierOrPlan.Explorer ||
        org.tier?.type === TierOrPlan.Ignite ||
        org.tier?.type === TierOrPlan.Students
    ) {
        return (
            <div>
                <div className={'flex flex-col gap-4 p-2 lg:grid lg:grid-cols-3 lg:gap-8 lg:p-6'}>
                    <div className={'lg:col-span-3'}>
                        <CustomPlan
                            tier={TierOrPlan.Custom}
                            billingCycle={billingCycle}
                            onSelect={() => {}}
                            loading={false}
                            orgTier={org.tier}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div>
                <div className={'mt-1 flex justify-center'}></div>
                <div className={'lg:px-18 flex flex-col gap-4 p-2 lg:grid lg:grid-cols-2 lg:gap-8 lg:p-4'}>
                    {/*                    <HobbyistPlan
                        key={'hobbyist-plan-' + (invoice?.id ?? 'empty')}
                        tier={currentTier}
                        onSelect={onPlanSelect}
                        billingCycle={billingCycle}
                        loading={loading && selectedTier === 'HOBBYIST'}
                        invoice={selectedTier === 'HOBBYIST' ? invoice : undefined}
                        onSwitch={(success) => {
                            setLoading(false);
                            setSelectedTier(undefined);
                            if (success) {
                                setCurrentTier(TierOrPlan.Hobbyist);
                                dispatch(refreshUserProfile());
                            } else {
                                setInvoice(undefined);
                            }
                        }}
                        expiry={org.tier?.type === TierOrPlan.Hobbyist ? org.tier.expiry : undefined}
                    />*/}
                    <ClassicPlan
                        key={'classic-plan-' + (invoice?.id ?? 'empty')}
                        tier={currentTier}
                        onSelect={onPlanSelect}
                        billingCycle={billingCycle}
                        loading={loading && selectedTier === 'PIXELCLASSIC'}
                        invoice={selectedTier === 'PIXELCLASSIC' ? invoice : undefined}
                        onSwitch={(success) => {
                            setLoading(false);
                            setSelectedTier(undefined);
                            if (success) {
                                setCurrentTier(TierOrPlan.PixelClassic);
                                dispatch(refreshUserProfile());
                            } else {
                                setInvoice(undefined);
                            }
                        }}
                        expiry={org.tier?.type === TierOrPlan.PixelClassic ? org.tier.expiry : undefined}
                    />
                    <StudioPlan
                        key={'studio-plan-' + (invoice?.id ?? 'empty')}
                        tier={currentTier}
                        onSelect={onPlanSelect}
                        billingCycle={billingCycle}
                        loading={loading && selectedTier === 'PIXELSTUDIO'}
                        invoice={selectedTier === 'PIXELSTUDIO' ? invoice : undefined}
                        onSwitch={(success) => {
                            setLoading(false);
                            setSelectedTier(undefined);
                            if (success) {
                                setCurrentTier(TierOrPlan.PixelStudio);
                                dispatch(refreshUserProfile());
                            } else {
                                setInvoice(undefined);
                            }
                        }}
                        expiry={org.tier?.type === TierOrPlan.PixelStudio ? org.tier.expiry : undefined}
                    />
                    {/*                    <ProPlan
                        key={'pro-plan-' + (invoice?.id ?? 'empty')}
                        tier={currentTier}
                        onSelect={onPlanSelect}
                        billingCycle={billingCycle}
                        loading={loading && selectedTier === 'PRO'}
                        invoice={selectedTier === 'PRO' ? invoice : undefined}
                        onSwitch={(success) => {
                            setLoading(false);
                            setSelectedTier(undefined);
                            if (success) {
                                setCurrentTier(TierOrPlan.Pro);
                                dispatch(refreshUserProfile());
                            } else {
                                setInvoice(undefined);
                            }
                        }}
                        expiry={org.tier?.type === TierOrPlan.Pro ? org.tier.expiry : undefined}
                    />*/}
                    {/*<div className={'lg:col-span-3'}>*/}
                    {/*                  <CustomPlan
                        tier={currentTier}
                        onSelect={onPlanSelect}
                        billingCycle={billingCycle}
                        loading={loading && selectedTier === 'CUSTOM'}
                    />*/}
                    {/*</div>*/}
                </div>
                <ToastContainer position={'top-right'} autoClose={3000} />
            </div>
            <Dialog open={contactUsPopup}>
                <DialogContent className={'block w-1/2 p-10'} onClose={onCloseContactUs}>
                    We will get in touch shortly. You can also reach out to us at{' '}
                    <a href={'mailto:support@perceptpixel.com'} className={'text-blue-500'}>
                        support@perceptpixel.com
                    </a>
                    .
                </DialogContent>
            </Dialog>
            <Dialog open={showWallet}>
                <DialogContent
                    className='w-[390px] p-0'
                    style={{borderRadius: '1rem'}}
                    onClose={() => {
                        setLoading(false);
                        setPayByWallet(false);
                        setShowWallet(false);
                    }}>
                    <div className='rounded-t-[4px] bg-[#404666] p-4'>
                        <img src={PixelLogoHeader} alt='pixel header' />
                    </div>
                    <div className='mx-3 mt-4 rounded-t-[4px] bg-[#404666] p-4 text-[12px] font-[600] text-white'>
                        Wallet Balance
                    </div>
                    <div className='mx-3 rounded-b-[4px] bg-[#F3F9FF] p-4 shadow-md'>
                        <div className='flex items-center gap-3'>
                            <input
                                type='checkbox'
                                checked={payByWallet}
                                onChange={() => setPayByWallet(!payByWallet)}
                                className='rounded-1 h-4 w-4 border border-[#D0D5DD] p-3'
                            />
                            <span className='text-[14px] font-[600]'>Pay using Percept’s Wallet Balance</span>
                        </div>
                        <div className='flex gap-2 px-6 py-2 text-[14px] font-[600] text-[#404666]'>
                            <WalletIcon />
                            {walletBalance} Pixel Points
                        </div>
                    </div>
                    <div className='my-6 flex items-center justify-center'>
                        <Button onClick={proceedToPayment}>Continue</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default function PixelPlansScreen() {
    const [rzpScriptReady, setRzpScriptReady] = React.useState(false);
    const mut = useRef(false);

    React.useEffect(() => {
        if (mut.current) {
            return;
        }
        mut.current = true;
        loadScript('https://checkout.razorpay.com/v1/checkout.js')
            .then((loaded) => {
                setRzpScriptReady(loaded);
            })
            .catch((err) => {
                console.error('failed to load razorpay script', err);
                setRzpScriptReady(false);
            });
    });

    return (
        <BaseScreen screenName={ScreenNames.BILLING_PLANS_SCREEN}>
            <UserLoader>
                {(user) => <PixelPlansScreenWithUser user={user} rzpScriptReady={rzpScriptReady} />}
            </UserLoader>
        </BaseScreen>
    );
}
